import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown, Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  buttonVariants,
  cn,
} from "@ando/ui";

import { readCachedSession } from "@/features/auth/session";

import type { WorkspaceTab } from "../../workspace-router/state/workspaceStore";
import { useCreateManualAction } from "../hooks/useCreateManualAction";
import { useManualActionCatalog } from "../hooks/useManualActionCatalog";

/**
 * A bespoke menu entry that doesn't go through the manual-action pipeline.
 *
 * Some area-native primitives aren't HITL action proposals — e.g. a Lavoro
 * "Nuovo task" opens the task composer, not an ``ActionType`` surface. Rather
 * than keep a *second* native button (the user wants ONE create button per
 * hub), the hub passes those primitives here so they live under the same
 * "Nuovo" menu, rendered above the catalog actions.
 */
export interface NewActionMenuExtraItem {
  readonly id: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly onSelect: () => void;
}

/**
 * NewActionMenu — per-area "Nuova azione" shortcut for a working-area hub.
 *
 * The contextual counterpart to the global Cmd+K "Nuova azione" group: drop
 * ``<NewActionMenu area="finance" />`` into the Finanza hub header and the
 * user gets a dropdown of exactly the manual actions for that area (Nuova
 * fattura / Nota di credito / Registra incasso). Both entry points render
 * from the SAME ``MANUAL_ACTION_CATALOG`` and start the SAME working-area
 * surface — no overlay, no duplicated wiring.
 *
 * Renders nothing when the area has no manual actions, so it's safe to drop
 * into every hub header unconditionally.
 */

export interface NewActionMenuProps {
  /** Working-area tab whose manual actions to offer. */
  readonly area: WorkspaceTab;
  /** Trigger label (default "Nuova azione"). */
  readonly label?: string;
  readonly className?: string;
  /** Area-native primitives (e.g. "Nuovo task") rendered above the catalog. */
  readonly extraItems?: readonly NewActionMenuExtraItem[];
}

export const NewActionMenu: React.FC<NewActionMenuProps> = ({
  area,
  label,
  className,
  extraItems,
}) => {
  const { i18n } = useLingui();
  const resolvedLabel = label ?? i18n._(msg`Nuova azione`);
  // Demo: hide ``create_record`` CRM/ERP actions — their proposal schema is
  // built from a LIVE connector (Salesforce/SAP), which the demo's dummy tokens
  // can't satisfy, so they'd error. The section guide explains what the area
  // does and offers working example prompts instead.
  const isDemo = Boolean(readCachedSession()?.is_demo);
  const catalog = useManualActionCatalog();
  const actions = React.useMemo(() => {
    const all = catalog.filter((action) => action.area === area);
    return isDemo ? all.filter((a) => a.input.action_type !== "create_record") : all;
  }, [area, catalog, isDemo]);
  const createManual = useCreateManualAction();

  const extras = extraItems ?? [];
  if (actions.length === 0 && extras.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(buttonVariants({ variant: "primary", size: "md" }), className)}
        aria-label={resolvedLabel}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        {resolvedLabel}
        <ChevronDown className="h-3.5 w-3.5 opacity-80" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        {/* Manual actions FIRST — they are the primary intent of the
            menu (the user came here to create a fatture / event /
            task). ``extraItems`` (settings shortcuts like "Configura
            connettori") live BELOW the separator so the destructive-
            adjacent / "navigate-away" entries don't sit at the top
            (Jun 12, 2026 user feedback). Typography aligned to
            ``QuickAddMenu`` so the two dropdowns feel identical:
            ``text-[13px]`` label, ``h-4 w-4`` icon. */}
        {actions.map((def) => {
          const Icon = def.icon;
          return (
            <DropdownMenuItem
              key={def.id}
              onSelect={() => createManual.mutate(def.input)}
              className="gap-2 text-[13px]"
            >
              <Icon className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
              <span className="truncate text-ink-1">{def.label}</span>
            </DropdownMenuItem>
          );
        })}
        {extras.length > 0 && actions.length > 0 ? (
          <DropdownMenuSeparator />
        ) : null}
        {extras.map((item) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem
              key={item.id}
              onSelect={item.onSelect}
              className="gap-2 text-[13px]"
            >
              <Icon className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
              <span className="truncate text-ink-1">{item.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
NewActionMenu.displayName = "NewActionMenu";
